import React from 'react';
import theme from '../../../theme';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import LockIcon from '@material-ui/icons/Lock';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import {Sidebar, SidebarBody, SidebarHead} from './Sidebar';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import List from '@material-ui/core/List';
import memoize from 'lodash-decorators/memoize';
import {sortBy} from 'lodash';
import {autobind} from 'core-decorators';
import isLayerTypeAuthorized from '../../../utils/gaia/isLayerTypeAuthorized';
import LayerListItem from '../LayerListItem';
import {UserLayerDispatcher} from '../../../hooks/useLayers';
import fetchUserLayerCategories from '../../../utils/gaia/fetchLayerTypeCategories';
import fetchUser from '../../../utils/gaia/fetchUser';
import trackLayerAnalytics from '../../../utils/gaia/trackLayerAnalytics';

export interface EditLayersSidebarProps extends React.HTMLAttributes<HTMLDivElement> {
    onSidebarChange: (sidebar: MapSidebar) => void;
    userLayers: UserLayer[] | undefined;
    userLayerDispatcher: UserLayerDispatcher;
    layerTypes: Record<string, LayerType> | undefined;
}

interface EditLayersSidebarState {
    expandedCategory: string | null;
    prevExpandedCategory: string | null;
    layerTypeCategories: LayerTypeCategory[] | undefined;
    user: User | undefined;
}

class EditLayersSidebar
    extends React.Component<EditLayersSidebarProps, EditLayersSidebarState> {

    constructor(props: EditLayersSidebarProps) {
        super(props);

        this.state = {
            expandedCategory: null,
            prevExpandedCategory: null,
            layerTypeCategories: undefined,
            user: undefined,
        };
    }

    async componentDidMount() {
        this.setState({
            layerTypeCategories: await fetchUserLayerCategories(),
            user: await fetchUser()
        });
    }

    render() {
        return <Sidebar {...this.props}>
            <SidebarHead
                onSidebarChange={this.props.onSidebarChange}
                prevSidebar={{type: 'layers'}}
                title={'Edit Layers'}
            />
            <SidebarBody>
                {this.renderBody()}
            </SidebarBody>
        </Sidebar>;
    }

    renderBody() {
        if (!this.state.layerTypeCategories) return null;
        return sortBy(this.state.layerTypeCategories, 'rank').map(this.renderCategory);
    }

    @autobind
    renderCategory(category: LayerTypeCategory) {
        if (!this.props.layerTypes) return null;

        const layerTypes = sortBy(this.props.layerTypes, [
            layerType => !this.state.user || !isLayerTypeAuthorized(layerType, this.state.user),
            layerType => layerType.title,
        ]).filter(layerType =>
            !layerType.deprecated && layerType.categories.includes(category.id)
        );

        if (layerTypes.length === 0) return null;

        let categoryNamePrefix = '';
        if (category.id === 'withgaiapro(overlays)-ng' || category.id === 'gaiaprobase-ng') {
            categoryNamePrefix = 'Premium ';
        }

        // Effectively set a max transition time for opening / closing categories.
        const timeout = layerTypes.length > 50 ?
            theme.transitions.duration.standard :
            'auto';

        return (<ExpansionPanel
            expanded={this.state.expandedCategory === category.id}
            onChange={this.onExpansionPanelChange(category.id)}
            key={category.id}
            TransitionProps={{timeout}}
            style={{
                backgroundColor: 'transparent' // prevent covering the scroll bar
            }}
        >
            <ExpansionPanelSummary
                style={{paddingLeft: 16, paddingRight: 16}}
                expandIcon={<ExpandMoreIcon style={{width: 24, height: 24}} />}
            >
                <div style={{
                    flexDirection: 'column', alignItems: 'flex-start'}}>
                    <Typography variant='body1' style={{marginBottom: theme.spacing(0.25)}}>
                        {categoryNamePrefix + category.name}
                    </Typography>
                    <Typography variant='body2' color='textSecondary'>
                        {layerTypes.length} layers
                    </Typography>
                </div>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails style={{padding: 0}}>
                <List style={{width: '100%'}}>
                    {this.renderCategoryLayerTypes(category, layerTypes)}
                </List>
            </ExpansionPanelDetails>
        </ExpansionPanel>);
    }

    renderCategoryLayerTypes(category: LayerTypeCategory, layerTypes: LayerType[]) {
        const {expandedCategory, prevExpandedCategory} = this.state;
        if (expandedCategory === category.id || prevExpandedCategory === category.id) {
            return layerTypes.map(layerType => this.renderLayer(layerType));
        } else {
            return null;
        }
    }

    renderLayer(layerType: LayerType) {
        return <LayerListItem
            layerType={layerType}
            key={layerType.uniqueTilecacheKey}
            sidebar={{type: 'editLayers'}}
            onSidebarChange={this.props.onSidebarChange}
        >
            {this.renderLayerActions(layerType)}
        </LayerListItem>;
    }

    renderLayerActions(layerType: LayerType) {
        if (!this.props.userLayers) return null;

        const isSelected = this.props.userLayers.find(((userLayer: UserLayer) =>
            layerType.uniqueTilecacheKey === userLayer.layer.type
        ));

        const {userLayerDispatcher} = this.props;

        if (isSelected) {
            if (layerType.categories[0] === 'custom') {
                return <IconButton
                    edge='end'
                    onClick={() => userLayerDispatcher({type: 'remove', layer: layerType})}
                    color='secondary'
                >
                    <DeleteForeverIcon style={{width: 24, height: 24}} />
                </IconButton>;
            } else {
                return <IconButton
                    edge='end'
                    onClick={() => userLayerDispatcher({type: 'remove', layer: layerType})}
                    color='secondary'
                >
                    <RemoveCircleIcon style={{width: 24, height: 24}} />
                </IconButton>;
            }
        } else if (!this.state.user || !isLayerTypeAuthorized(layerType, this.state.user)) {
            return <IconButton
                edge='end'
                disabled
            >
                <LockIcon style={{width: 24, height: 24}} />
            </IconButton>;
        } else {
            return (<IconButton
                edge='end'
                onClick={() => userLayerDispatcher({type: 'add', layer: layerType})}
                color='primary'
            >
                <AddCircleIcon style={{width: 24, height: 24}} />
            </IconButton>);
        }
    }

    @memoize
    @autobind
    onExpansionPanelChange(id: string) {
        return (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
            trackLayerAnalytics('Open Map Library Category', this.props.userLayers, {
                categoryName: id,
                category: 'map2',
            });
            this.setState({
                expandedCategory: isExpanded ? id : null,
                prevExpandedCategory: this.state.expandedCategory
            });
        };
    }
}

export default React.memo(EditLayersSidebar);
